/**
 * #787 §5 — the promotion review. Shown after Manage is granted on a folder
 * when grants INSIDE it still leave someone that grant covers below Manage
 * (the server lists them: `narrowings` on `POST /api/folders/:id/permissions`).
 *
 * Nothing is lifted automatically — an old narrowing may be deliberate — so
 * the default is to KEEP every row. The reviewer may lift a PERSONAL one they
 * manage (it removes that grant on the subfolder, through the ordinary DELETE
 * with all its checks); rows they can't change are shown locked. A team or role
 * row is review-only: deleting it would widen access for everyone it covers.
 */
import { useState } from 'react'
import { Folder, Lock, X } from 'lucide-react'
import { request } from '@papan/api-client'
import { useToast } from '@papan/core-ui/ui'
import { ROLE_LABELS } from './WhoHasAccessDialog.types.js'

export interface Narrowing {
  permId: string
  folderId: string
  folderPath: string
  targetType: string
  targetId: string
  permission: string
  locked: boolean
  /** A team or role row — lifting it would widen access for everyone it covers. */
  sharedRow: boolean
}

const PERM_TEXT: Record<string, string> = {
  hidden: 'No access',
  readcopy: 'Read only',
  readwrite: 'Read & Write',
  admin: 'Manage & Edit',
}

function principalText(n: Narrowing, teamName: (id: string) => string | undefined): string {
  if (n.targetType === 'role') return `${ROLE_LABELS[n.targetId] ?? n.targetId} (role)`
  if (n.targetType === 'group') return `${teamName(n.targetId) ?? 'A team'} (team)`
  return n.targetId
}

export function NarrowingReview({ granteeLabel, narrowings, teamName, onClose }: {
  granteeLabel: string
  narrowings: Narrowing[]
  teamName: (id: string) => string | undefined
  onClose: () => void
}) {
  const toast = useToast()
  const [rows, setRows] = useState(narrowings)
  const [busy, setBusy] = useState<string | null>(null)

  const lift = async (n: Narrowing) => {
    setBusy(n.permId)
    try {
      await request(`/api/folders/${n.folderId}/permissions/${n.permId}`, { method: 'DELETE' })
      setRows(curr => curr.filter(r => r.permId !== n.permId))
    } catch (err) {
      toast.error(err instanceof Error && err.message ? err.message : 'Failed to lift this limit')
    } finally {
      setBusy(null)
    }
  }

  return (
    <div className="absolute inset-0 z-30 flex items-center justify-center p-4 bg-black/40 rounded-none md:rounded-lg" onClick={onClose}>
      <div
        role="dialog"
        aria-label="Still limited inside this folder"
        className="themed-bg-secondary themed-border border rounded-lg shadow-2xl w-full max-w-lg max-h-full flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 px-5 pt-4 pb-2">
          <div className="min-w-0">
            <h4 className="text-sm font-semibold themed-text">Still limited inside this folder</h4>
            <p className="text-xs themed-text-muted mt-1">
              {granteeLabel} now has Manage &amp; Edit here, but these older grants inside it keep them below Manage.
              They stay unless you lift them.
            </p>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" className="p-1 themed-text-muted hover:themed-text rounded shrink-0">
            <X size={16} />
          </button>
        </div>
        <ul className="px-5 py-2 overflow-y-auto flex-1 divide-y themed-border">
          {rows.map(n => (
            <li key={n.permId} className="py-2 flex items-center gap-3">
              <Folder size={14} className="text-yellow-500 shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="text-sm themed-text truncate" title={n.folderPath}>{n.folderPath}</div>
                <div className="text-xs themed-text-muted truncate">
                  {principalText(n, teamName)} → {PERM_TEXT[n.permission] ?? n.permission}
                </div>
              </div>
              {n.sharedRow ? (
                <span className="text-xs themed-text-muted shrink-0 max-w-[9rem] text-right" title="Removing it would give everyone in this team or role more access, not just this person. Change it from that folder's access dialog.">
                  Applies to the whole {n.targetType === 'role' ? 'role' : 'team'}
                </span>
              ) : n.locked ? (
                <span className="flex items-center gap-1 text-xs themed-text-muted shrink-0" title="You don't manage this subfolder, or the grant is locked above your level">
                  <Lock size={12} /> Locked
                </span>
              ) : (
                <button
                  type="button"
                  disabled={busy !== null}
                  onClick={() => lift(n)}
                  className="px-2.5 py-1 text-xs rounded themed-btn-secondary disabled:opacity-50 shrink-0"
                >
                  {busy === n.permId ? 'Lifting…' : 'Lift'}
                </button>
              )}
            </li>
          ))}
          {rows.length === 0 && (
            <li className="py-3 text-xs themed-text-muted">Nothing left to review.</li>
          )}
        </ul>
        <div className="px-5 pb-4 pt-2 flex justify-end">
          <button type="button" onClick={onClose} className="px-4 py-1.5 text-sm rounded-lg bg-blue-600 hover:bg-blue-700 text-white">
            Keep the rest
          </button>
        </div>
      </div>
    </div>
  )
}
